import React, { useState, useEffect, useRef } from 'react';
import { Crown, Edit3, Eye } from 'lucide-react';
import { getUserRole, canEdit as checkCanEdit, getRoleBadge } from './types/sharingUtils';
import type { UserRole } from './types';

/**
 * Tracks the current user's role and edit permission for a given flowchart/mindmap.
 * Returns a ref in addition to state so closures (setInterval, event handlers)
 * always read the latest value without re-subscribing.
 */
export function useFlowchartAccess(currentId: string | null) {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [canEdit, setCanEdit] = useState<boolean>(true);
  const canEditRef = useRef<boolean>(true);

  useEffect(() => { canEditRef.current = canEdit; }, [canEdit]);

  useEffect(() => {
    const check = async () => {
      if (!currentId) {
        setUserRole('owner');
        setCanEdit(true);
        return;
      }
      const role = await getUserRole(currentId);
      setUserRole(role);
      const editable = await checkCanEdit(currentId);
      setCanEdit(editable);
    };
    check();
  }, [currentId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { userRole, canEdit, canEditRef };
}

/**
 * Small badge that shows the current user's role (owner / editor / viewer).
 * Only rendered when both currentId and role are known.
 */
export function RoleBadge({ currentId, userRole }: { currentId: string | null; userRole: UserRole | null }) {
  if (!currentId || !userRole) return null;

  const badge = getRoleBadge(userRole);
  const bg =
    userRole === 'owner' ? 'linear-gradient(to bottom, #b3dde4, #82C0CC)' :
    userRole === 'editor' ? 'linear-gradient(to bottom, #ffe5b0, #ffd080)' :
    'linear-gradient(to bottom, #dff0f3, #c9e8ed)';
  const color =
    userRole === 'owner' ? '#16697A' :
    userRole === 'editor' ? '#7a4a00' :
    '#489FB5';

  return (
    <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium" style={{ background: bg, color, flexShrink: 0 }}>
      {userRole === 'owner' && <Crown size={12} />}
      {userRole === 'editor' && <Edit3 size={12} />}
      {userRole === 'viewer' && <Eye size={12} />}
      <span>{badge.label}</span>
    </div>
  );
}
